import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  PhotoIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';

interface Destination {
  _id: string;
  name: string;
  image: string;
  description?: string;
  isActive: boolean;
  showInMainNav?: boolean; // Show in main navbar vs Other Destinations
  resortCount?: number; // Optional - calculated from database
  createdAt: string;
}

const AdminDestinations: React.FC = () => {
  const { token } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editingDestination, setEditingDestination] = useState<Destination | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [imagePreview, setImagePreview] = useState<string>('');

  // Real destination data from database
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);

  // Resolve image URLs for uploads served from the backend
  const resolveImageUrl = (img?: string) => {
    if (!img) return '';
    if (img.startsWith('http')) return img;
    // Normalize any Windows path to forward slashes
    const normalized = img.replace(/\\/g, '/');
    // If absolute path contains '/uploads/', extract that portion
    const idx = normalized.indexOf('/uploads/');
    const relPath = idx !== -1
      ? normalized.slice(idx)
      : (normalized.startsWith('/') ? normalized : `/${normalized}`);
    const isDev = window.location.port === '3000';
    const base = isDev ? 'http://localhost:5000' : '';
    return `${base}${relPath}`;
  };

  // Fetch real destinations from database
  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        setLoading(true);
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const response = await fetch('/api/destinations', { headers });
        if (response.ok) {
          const data = await response.json();
          // Handle both array response and object with destinations property
          const destinationsData = data.destinations || data;
          setDestinations(Array.isArray(destinationsData) ? destinationsData : []);
        } else {
          setDestinations([]);
        }
      } catch (error) {
        console.error('Error fetching destinations:', error);
        setDestinations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDestinations();
  }, [token]);

  const handleAdd = () => {
    setEditingDestination(null);
    setShowModal(true);
  };

  const handleEdit = (destination: Destination) => {
    setEditingDestination(destination);
    setImagePreview(destination.image || '');
    setShowModal(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setEditingDestination(null);
    setImagePreview('');
  };

  const handleSave = async (e: React.FormEvent) => {
    console.log('Form submitted!'); // Debug log
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    
    try {
      console.log(editingDestination ? 'Updating destination...' : 'Creating new destination...'); // Debug log

      // Using axios instead of fetch for better error handling
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      let response;
      const url = `${baseURL}/api/destinations${editingDestination ? `/${editingDestination._id}` : ''}`;
      console.log('Making request to:', url);

      if (editingDestination) {
        response = await axios.put(url, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await axios.post(url, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      console.log('API Response status:', response.status); // Debug log

      if (response.status >= 200 && response.status < 300) {
        const result = response.data;
        console.log('API Response data:', result); // Debug log
        const newDestination = result.destination;
        
        if (editingDestination) {
          setDestinations(destinations.map(dest => 
            dest._id === editingDestination._id ? newDestination : dest
          ));
        } else {
          setDestinations([...destinations, newDestination]);
        }
        
        resetModal();
        alert('Destination saved successfully!');
      } else {
        console.error('API Error:', response.data); // Debug log
        alert(response.data?.message || 'Failed to save destination');
      }
    } catch (error) {
      console.error('Error saving destination:', error);
      alert('Failed to save destination: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleToggleStatus = (id: string) => {
    setDestinations(Array.isArray(destinations) ? destinations.map(dest => 
      dest._id === id ? { ...dest, isActive: !dest.isActive } : dest
    ) : []);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this destination?')) {
      setDestinations(Array.isArray(destinations) ? destinations.filter(dest => dest._id !== id) : []);
    }
  };

  return (
    <>
      <Helmet>
        <title>Destinations - Bali Getaway Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Destinations</h1>
            <p className="text-gray-600">Manage travel destinations and locations</p>
          </div>
          <button
            onClick={handleAdd}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Destination</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-600">{destinations.length}</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Destinations</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-green-600">
                    {Array.isArray(destinations) ? destinations.filter(d => d.isActive).length : 0}
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Destinations</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-secondary-600">
                    {Array.isArray(destinations) ? destinations.reduce((sum, d) => sum + (d.resortCount || 0), 0) : 0}
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Resorts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Destinations Grid */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">All Destinations</h2>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-4">Loading destinations...</p>
            </div>
          ) : !Array.isArray(destinations) || destinations.length === 0 ? (
            <div className="text-center py-12">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No destinations</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new destination.</p>
              <div className="mt-6">
                <button onClick={handleAdd} className="btn-primary">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Destination
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {Array.isArray(destinations) && destinations.map((destination) => (
                <div key={destination._id} className="bg-gray-50 rounded-lg overflow-hidden">
                  <div className="relative">
                    <img
                      src={resolveImageUrl(destination.image)}
                      alt={destination.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute top-2 right-2 flex flex-col gap-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        destination.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {destination.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {destination.showInMainNav && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          <Bars3Icon className="h-3 w-3" />
                          Main Nav
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {destination.name}
                    </h3>
                    
                    {destination.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {destination.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span>{destination.resortCount || 0} resorts</span>
                      <span>Added {new Date(destination.createdAt).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(destination)}
                        className="flex-1 btn-primary flex items-center justify-center space-x-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                        <span>Edit</span>
                      </button>
                      
                      <button
                        onClick={() => handleToggleStatus(destination._id)}
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center ${
                          destination.isActive
                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                        title={destination.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {destination.isActive ? (
                          <EyeSlashIcon className="h-4 w-4" />
                        ) : (
                          <EyeIcon className="h-4 w-4" />
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleDelete(destination._id)}
                        className="px-3 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 flex items-center justify-center"
                        title="Delete"
                        disabled={(destination.resortCount || 0) > 0}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {(destination.resortCount || 0) > 0 && (
                      <p className="text-xs text-red-600 mt-2">
                        Cannot delete: {destination.resortCount || 0} resorts associated
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative my-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingDestination ? 'Edit Destination' : 'Add New Destination'}
              </h3>
              
              <form className="space-y-4" onSubmit={handleSave}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destination Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    placeholder="Enter destination name"
                    defaultValue={editingDestination?.name || ''}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destination Image
                  </label>
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    className="form-input"
                    onChange={handleImageUpload}
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload an image for the destination</p>
                  
                  {(imagePreview || editingDestination?.image) && (
                    <div className="mt-3">
                      <img 
                        src={imagePreview || editingDestination?.image} 
                        alt="Preview" 
                        className="h-32 w-48 object-cover rounded border border-gray-300"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {imagePreview && imagePreview !== editingDestination?.image ? 'New image preview' : 'Current image'}
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    className="form-input"
                    rows={4}
                    placeholder="Enter destination description"
                    defaultValue={editingDestination?.description || ''}
                  />
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                      defaultChecked={editingDestination?.isActive ?? true}
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                      Active destination
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="showInMainNav"
                      name="showInMainNav"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      defaultChecked={editingDestination?.showInMainNav ?? false}
                    />
                    <label htmlFor="showInMainNav" className="ml-2 block text-sm text-gray-700">
                      <span className="font-medium">Show in Main Navigation</span>
                      <span className="block text-xs text-gray-500">Display directly in navbar (e.g., Kuta, Seminyak). Others go under "Other Destinations"</span>
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={resetModal}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    {editingDestination ? 'Update' : 'Create'} Destination
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDestinations;
